'use client';

import {
  addFirmRole,
  deleteFirmRoleAssignment,
} from '@/_api/admin-firm-role-assignments';
import { UserContext } from '@/_contexts/UserProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { FirmUser } from '@/_types/models/user';
import { Company } from '@/_types/models/company';
import { Button, ButtonGroup, Icon, Flex, Text, Combobox } from '@/_components';
import { useMemo, useCallback, useContext, useState, type FC } from 'react';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { CompanyContext } from '@/_contexts/admin/CompanyProvider';
import type { FirmRoleItemProps } from './FirmRoleItem.types';
import type { ResultListItem } from '@/_components/ResultList/ResultList.types';
import { EntityV2Context } from '@/_contexts/admin/EntityV2Provider';
import styles from './FirmRoleItem.module.css';
import { getClasses } from '@/_utils/classnames';
import { RemoveFirmRoleAssignmentDialog } from './_components/RemoveFirmRoleAssignmentDialog';
import { FirmRoleAssignment } from '@/_types/models/firmRole';

export const FirmRoleItem: FC<FirmRoleItemProps> = ({
  company,
  isEditing,
  firmRole,
}) => {
  const { firmUserOptions, firmUsers } = useContext(FirmSettingsContext);
  const { token } = useContext(UserContext);
  const { addToast } = useContext(ToastContext);
  const { updateSelectedCompany } = useContext(CompanyContext);

  const { setEditEntity } = useContext(EntityV2Context);

  const selectedFirmUserIds: Array<number | undefined> = useMemo(
    () =>
      company?.firm_role_assignments
        ?.filter((fra) => fra.firm_role_id === firmRole.id)
        ?.map((fra) => fra.user_id) || [],
    [company?.firm_role_assignments, firmRole?.id],
  );

  const [selectedFirmRoleAssignment, setSelectedFirmRoleAssignment] =
    useState<Partial<FirmRoleAssignment | undefined>>(undefined);
  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [loadingId, setLoadingId] = useState<number | undefined>(undefined);
  const [value, setValue] = useState<string>('');

  const addFirmRoleAssignment = useCallback(
    async (newUserId: number, firmRoleId: number) => {
      try {
        setLoadingId(firmRoleId);

        const { errors, id, firm_role_id } = await addFirmRole({
          firmRoleId,
          companyId: company?.id!,
          userId: newUserId,
          token,
        });

        if (!!errors?.length) {
          addToast('info', errors?.[0], { error: errors });
          return;
        }

        const newFirmRoleAssignments: Company['firm_role_assignments'] = [
          ...(company?.firm_role_assignments || []),
          {
            id,
            user_id: newUserId,
            firm_role_id,
          },
        ];

        const updatedCompanyWithAssignments: Company = {
          ...company,
          firm_role_assignments: newFirmRoleAssignments,
        };

        updateSelectedCompany(updatedCompanyWithAssignments);
        setEditEntity(updatedCompanyWithAssignments);

        addToast('success', null);
        setIsAdding(false);
        setValue('');
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoadingId(undefined);
      }
    },
    [addToast, setEditEntity, company, updateSelectedCompany, token],
  );

  const removeFirmRoleAssignment = useCallback(
    async (firmRoleId: number, userId: number) => {
      try {
        setLoadingId(firmRoleId);
        const { error } = await deleteFirmRoleAssignment({
          firmRoleId,
          userId,
          token,
        });

        if (!error) {
          const newFirmRoleAssignments: Company['firm_role_assignments'] = [
            ...(company?.firm_role_assignments || []),
          ].filter(
            (fra) =>
              !(fra.firm_role_id === firmRoleId && fra.user_id === userId),
          );

          const updatedCompanyWithAssignments: Company = {
            ...company,
            firm_role_assignments: newFirmRoleAssignments,
          };

          updateSelectedCompany(updatedCompanyWithAssignments);
          setEditEntity(updatedCompanyWithAssignments);

          addToast('success', null);
        }
      } catch (error) {
        addToast('error', null, { error });
      } finally {
        setLoadingId(undefined);
        setValue('');
      }
    },
    [addToast, setEditEntity, company, token, updateSelectedCompany],
  );

  const handleSelect = useCallback(
    (item: ResultListItem) => {
      const userId = item.value ? parseInt(item.value) : -1;
      if (userId !== -1) {
        addFirmRoleAssignment(userId, firmRole.id);
      }
    },
    [addFirmRoleAssignment, firmRole.id],
  );

  const comboboxItems: Array<ResultListItem> = useMemo(() => {
    return firmUserOptions
      .filter((option) => !selectedFirmUserIds.includes(parseInt(option.value)))
      .map((option) => ({
        name: option.name,
        value: option.value.toString(),
      }));
  }, [firmUserOptions, selectedFirmUserIds]);

  const selectedUsers: Array<FirmUser> = useMemo(
    () => firmUsers.filter((fu) => selectedFirmUserIds.includes(fu.id)),
    [firmUsers, selectedFirmUserIds],
  );

  const onClear = () => setValue('');

  const onToggleAdd = () => setIsAdding((previous) => !previous);

  const firmRolePillClasses: string = getClasses(styles.firmRoleItem__pill, {
    [styles.firmRoleItem__pillEditing]: isEditing,
  });

  const onCloseConfirmDeleteFirmRoleAssignment = () => {
    setSelectedFirmRoleAssignment(undefined);
  };

  const firmRoleConfirmUserName: string =
    firmUsers?.find((f) => f.id === selectedFirmRoleAssignment?.user_id)
      ?.name || 'User';

  return (
    <>
      <RemoveFirmRoleAssignmentDialog
        isOpen={!!selectedFirmRoleAssignment}
        firmRoleAssignment={selectedFirmRoleAssignment}
        onClose={onCloseConfirmDeleteFirmRoleAssignment}
        removeFirmRoleAssignment={removeFirmRoleAssignment}
        firmRole={firmRole}
        userName={firmRoleConfirmUserName}
      />

      <Flex isFullWidth={true} isColumn={true} gap={4} yAlign="end" padding={4}>
        <Flex isBetween={true} gap={4} isFullWidth={true} isYCentered={true}>
          <Flex isYCentered={true} gap={2}>
            <Icon name="shield-check" size={20} color="secondary" />
            <Text
              type="small"
              fontWeight="medium"
              isEllipsis={true}
              className={styles.firmRoleItem__text}
            >
              {firmRole.role_name}
            </Text>
          </Flex>
          <Flex gap={2} isWrapping={true} xAlign="end">
            {selectedUsers?.map((user, key) => {
              const deleteFirmRoleAssignment = () => {
                const selectedAssignment:
                  | Partial<FirmRoleAssignment>
                  | undefined = company?.firm_role_assignments?.find(
                  (fra) =>
                    fra.user_id === user.id && fra.firm_role_id === firmRole.id,
                );

                setSelectedFirmRoleAssignment(selectedAssignment);
              };

              return (
                <Flex
                  key={key}
                  className={firmRolePillClasses}
                  isYCentered={true}
                  isBetween={true}
                  isInline={true}
                  gap={4}
                >
                  <Flex isYCentered={true} gap={2}>
                    <Icon
                      name="user-circle"
                      size={20}
                      color={user?.name ? 'accent' : 'secondary'}
                    />
                    <Text
                      type="tiny"
                      fontWeight="medium"
                      color="accent"
                      noWrap={true}
                    >
                      {user?.name || 'Unknown'}
                    </Text>
                  </Flex>
                  {isEditing && (
                    <ButtonGroup>
                      <Button
                        value="Remove"
                        onClick={deleteFirmRoleAssignment}
                        size="tiny"
                        isIconOnly={true}
                        iconLeft="x"
                        intent="ghost"
                      />
                    </ButtonGroup>
                  )}
                </Flex>
              );
            })}
            {isEditing && (
              <ButtonGroup>
                <Button
                  value="Add"
                  size="tiny"
                  isIconOnly={true}
                  onClick={onToggleAdd}
                  iconLeft={isAdding ? 'minus' : 'plus'}
                  intent="secondary"
                  withTooltip={comboboxItems.length === 0}
                  tooltip="Every firm user has this role"
                  isDisabled={comboboxItems.length === 0}
                />
              </ButtonGroup>
            )}
          </Flex>
        </Flex>
        {isEditing && isAdding && (
          <Flex>
            <Combobox
              label="Select firm user"
              isLabelHidden={true}
              items={comboboxItems}
              iconLeft="user-circle"
              onSelect={handleSelect}
              placeholder="Select firm user"
              setValue={setValue}
              onClear={onClear}
              isDisabled={loadingId === firmRole?.id}
              value={value}
            />
          </Flex>
        )}
      </Flex>
    </>
  );
};
